import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Eye, EyeOff, LockKeyhole, Mail, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { api, unwrap, type StaffAuthenticated } from '@/api/client';
import { ApiError, mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';
import { useAuth } from '@/auth/AuthProvider';
import { recaptchaHeader, recaptchaToken } from '@/auth/recaptcha';
import { InputOTP, InputOTPSlot } from '@/components/ui/input-otp';

import { AuthLayout, Campo } from './AuthLayout';

type Challenge = components['schemas']['StaffChallenge'];
type LoginResult = components['schemas']['StaffLoginResult'];

const schema = z.object({
  email: z.string().trim().min(1, 'Escribe tu correo').email('Escribe un correo válido'),
  password: z.string().min(1, 'Escribe tu contraseña'),
});
type Form = z.infer<typeof schema>;

const AVISOS: Record<string, string> = {
  SESSION_REVOKED: 'Tu sesión se cerró desde otro lugar. Si no fuiste tú, cambia tu contraseña.',
  SESSION_EXPIRED: 'Tu sesión terminó. Inicia sesión de nuevo.',
};

export function LoginPage() {
  const { signIn, endedReason, clearEndedReason } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [ver, setVer] = useState(false);
  const [reto, setReto] = useState<Challenge | null>(null);

  useEffect(() => {
    const aviso = endedReason ? AVISOS[endedReason] : undefined;
    if (aviso) toast.warning(aviso, { id: 'sesion-terminada', duration: 8000 });
    if (endedReason) clearEndedReason();
  }, [endedReason, clearEndedReason]);

  const entrar = (result: StaffAuthenticated) => {
    signIn(result);
    const desde = (location.state as { desde?: string } | null)?.desde;
    void navigate(desde ?? result.staff.startUrl ?? '/', { replace: true });
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Form>({ resolver: zodResolver(schema) });

  const onSubmit = async (form: Form) => {
    try {
      const token = await recaptchaToken('admin_login');
      const result = await unwrap<LoginResult>(
        api.POST('/api/v1/admin/auth/login', { body: form, headers: recaptchaHeader(token) }),
      );
      if (result.status === 'authenticated') entrar(result);
      else setReto(result);
    } catch (err) {
      toast.error(mensajeDe(err));
    }
  };

  if (reto) return <PasoCodigo reto={reto} onVolver={() => setReto(null)} onListo={entrar} />;

  return (
    <AuthLayout titulo="Inicia sesión">
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4" noValidate>
        <Campo etiqueta="Correo electrónico" error={errors.email?.message}>
          <span className="relative block">
            <Mail
              size={18}
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-texto-3"
            />
            <input
              type="email"
              autoComplete="username"
              className="input-vidrio pl-10"
              placeholder="tu@consaludenarm.com"
              aria-invalid={!!errors.email}
              {...register('email')}
            />
          </span>
        </Campo>
        <Campo etiqueta="Contraseña" error={errors.password?.message}>
          <span className="relative block">
            <LockKeyhole
              size={18}
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-texto-3"
            />
            <input
              type={ver ? 'text' : 'password'}
              autoComplete="current-password"
              className="input-vidrio pr-11 pl-10"
              placeholder="Tu contraseña"
              aria-invalid={!!errors.password}
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setVer((v) => !v)}
              className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full p-1.5 text-texto-3 hover:text-texto"
              aria-label={ver ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {ver ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </span>
        </Campo>
        <div className="text-right">
          <Link to="/recuperar" className="text-sm font-semibold">
            ¿Olvidaste tu contraseña?
          </Link>
        </div>
        <button
          type="submit"
          className="btn-cs btn-primario w-full py-3 text-base"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Entrando…' : 'Iniciar sesión'}
        </button>
        <p className="text-center text-xs text-texto-3">
          Acceso exclusivo para el equipo de Consalud. Toda la actividad queda registrada.
        </p>
      </form>
    </AuthLayout>
  );
}

function PasoCodigo({
  reto,
  onVolver,
  onListo,
}: {
  reto: Challenge;
  onVolver: () => void;
  onListo: (r: StaffAuthenticated) => void;
}) {
  const [codigo, setCodigo] = useState('');
  const [confiar, setConfiar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [espera, setEspera] = useState(60);

  useEffect(() => {
    if (espera <= 0) return;
    const t = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [espera]);

  const verificar = async (code: string) => {
    if (code.length !== 6) return;
    setEnviando(true);
    try {
      onListo(
        await unwrap<StaffAuthenticated>(
          api.POST('/api/v1/admin/auth/2fa/verify', {
            body: { challengeToken: reto.challengeToken, code, trustDevice: confiar },
          }),
        ),
      );
    } catch (err) {
      toast.error(mensajeDe(err));
      setCodigo('');
      if (
        err instanceof ApiError &&
        ['CHALLENGE_INVALID', 'OTP_TOO_MANY_ATTEMPTS'].includes(err.code)
      )
        onVolver();
    } finally {
      setEnviando(false);
    }
  };

  const reenviar = async () => {
    try {
      const token = await recaptchaToken('admin_resend_code');
      await unwrap(
        api.POST('/api/v1/admin/auth/2fa/resend', {
          body: { challengeToken: reto.challengeToken },
          headers: recaptchaHeader(token),
        }),
      );
      toast.success('Te enviamos un código nuevo.');
      setEspera(60);
    } catch (err) {
      toast.error(mensajeDe(err));
    }
  };

  return (
    <AuthLayout titulo="Confirma que eres tú">
      <div className="space-y-5 text-center">
        <ShieldCheck className="mx-auto text-cian-texto" size={36} aria-hidden />
        <p className="text-texto-2">
          Escribe el código de 6 dígitos que enviamos a <strong>{reto.emailMasked}</strong>.
        </p>
        <div className="flex justify-center">
          <InputOTP
            maxLength={6}
            value={codigo}
            onChange={setCodigo}
            onComplete={(v: string) => void verificar(v)}
            disabled={enviando}
            autoFocus
            inputMode="numeric"
            pattern="^[0-9]*$"
            aria-label="Código de verificación"
          >
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot key={i} index={i} />
            ))}
          </InputOTP>
        </div>
        <label className="flex items-center justify-center gap-2 text-sm text-texto-2">
          <input
            type="checkbox"
            checked={confiar}
            onChange={(e) => setConfiar(e.target.checked)}
            className="h-4 w-4 accent-[var(--color-azul-600)]"
          />
          Confiar en este navegador por 30 días
        </label>
        <button
          type="button"
          className="btn-cs btn-primario w-full"
          disabled={enviando || codigo.length !== 6}
          onClick={() => void verificar(codigo)}
        >
          {enviando ? 'Verificando…' : 'Entrar'}
        </button>
        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            className="flex items-center gap-1 font-semibold"
            onClick={onVolver}
          >
            <ArrowLeft size={16} aria-hidden /> Volver
          </button>
          <button
            type="button"
            className="font-semibold disabled:opacity-50"
            disabled={espera > 0}
            onClick={() => void reenviar()}
          >
            {espera > 0 ? `Reenviar en ${espera} s` : 'Reenviar código'}
          </button>
        </div>
      </div>
    </AuthLayout>
  );
}
